import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { Problem } from '../api/types';
import { toProblem } from '../app/useAsync';
import { Button } from './Button';
import { ProblemNotice } from './ProblemNotice';

export interface ConfirmDialogProps {
  /** 見出し。ダイアログの読み上げ名にもなる。 */
  title: string;
  /** 何が起きるか、どう戻せるか。 */
  body: ReactNode;
  /** 実行ボタンの文言（「削除する」「この版に戻す」）。 */
  confirmLabel: string;
  /**
   * 実行。成功したら呼び出し側がダイアログを外す。冪等キーは開いている間ずっと同じ値を渡すので、
   * 通信断の後の再試行が同じ操作として扱われる。
   */
  onConfirm(idempotencyKey: string): Promise<void>;
  /** 失敗の意味と次の行動。null なら problem だけを出す。 */
  describeFailure?(problem: Problem): string | null;
  onClose(): void;
}

/**
 * 戻せる操作の確認ダイアログ（Draft のファイル削除・版の restore）。
 *
 * 取り消せない削除の DeleteDialog と違い、名前の入力は求めない（どちらも版の履歴から戻せる）。
 * ネイティブの<dialog>をshowModal()で開き、背景の操作不可とEscでの取消はブラウザに任せる。
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  describeFailure,
  onClose,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  // StrictMode で effect が 2 回走っても開き直さないよう open を見る。
  // cleanup では close() しない。close イベントが親へ伝わり、開いたばかりのダイアログを閉じてしまうため。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }, []);

  const confirm = async () => {
    if (running) return;
    setRunning(true);
    setProblem(null);
    try {
      await onConfirm(idempotencyKey);
      // 成功後は呼び出し側が外す。それまで二重に押せないよう実行中のままにする。
    } catch (error) {
      setProblem(toProblem(error));
      setRunning(false);
    }
  };

  // 閉じる経路はネイティブの close に一本化する（キャンセルも Esc もここを通る）。
  const cancel = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  const hint = problem ? (describeFailure?.(problem) ?? null) : null;
  // 409・503・通信断は、読み込み直すか待てば解ける。赤ではなく注意色で出す。
  const tone =
    problem && (problem.status === 0 || problem.status === 409 || problem.status === 503)
      ? 'notice'
      : 'error';

  return (
    <dialog
      ref={dialogRef}
      className="dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        if (running) event.preventDefault();
      }}
      onClose={() => {
        // Chrome は続けて押された Esc を cancel で止められずに閉じることがある。実行中なら開き直す。
        if (running) {
          dialogRef.current?.showModal();
          return;
        }
        onClose();
      }}
    >
      <div className="dialog-head">
        <h2 id={titleId} className="dialog-title">
          {title}
        </h2>
      </div>
      <div className="dialog-body">
        <div className="dialog-description">{body}</div>
        <ProblemNotice problem={problem} tone={tone} />
        {hint === null ? null : <p className="dialog-hint">{hint}</p>}
      </div>
      <div className="dialog-actions">
        <Button disabled={running} onClick={cancel}>
          キャンセル
        </Button>
        <Button variant="primary" disabled={running} onClick={() => void confirm()}>
          {running ? '処理中…' : confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}
